/**
 * 清溪天成模版 - 单页
 * 
 * 设计：水波装饰头部 + 居中型内容卡片
 * 独特排版：页面顶部使用双色水波SVG装饰，内容区居中使用窄列排版，宽度更适合阅读
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/crystal/components/Navbar";
import Footer from "@/templates/crystal/components/Footer";
import { getSinglePage } from "@/utils/api";

export default function CrystalSinglePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getSinglePage(slug)
      .then((data) => setPage(data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingSkeleton />;
  if (!page) return <NotFound />;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* 装饰头部 */}
      <section className="relative pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-50/60 to-white pointer-events-none" />
        {/* 波浪 */}
        <svg className="absolute bottom-0 inset-x-0 text-cyan-50/40" viewBox="0 0 1440 120" fill="currentColor" preserveAspectRatio="none">
          <path d="M0 80 Q 180 40, 360 70 T 720 50 T 1080 65 T 1440 40 L 1440 120 L 0 120 Z" />
        </svg>
        <svg className="absolute bottom-0 inset-x-0 text-blue-50/30" viewBox="0 0 1440 120" fill="currentColor" preserveAspectRatio="none">
          <path d="M0 95 Q 240 60, 480 80 T 960 65 T 1440 75 L 1440 120 L 0 120 Z" />
        </svg>

        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{page.title}</h1>
          <div className="flex items-center justify-center gap-1.5 mt-3 text-sm text-gray-400">
            <Link to="/" className="hover:text-cyan-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-gray-600">{page.title}</span>
          </div>
        </div>
      </section>

      {/* 内容 */}
      <article className="relative pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-gray-100 shadow-sm">
            {page.content ? (
              <div
                className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-p:leading-loose prose-a:text-cyan-600 prose-a:no-underline hover:prose-a:text-cyan-700 prose-strong:text-gray-800 prose-img:rounded-xl"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-cyan-50">
                  <i className="ri-file-text-line text-2xl text-cyan-400" />
                </div>
                <p className="text-gray-400">暂无内容</p>
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-cyan-600 transition-colors group"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform" />
              返回首页
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-gradient-to-b from-cyan-50/60 to-white pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-6 text-center animate-pulse">
          <div className="w-48 h-7 bg-cyan-100/60 rounded-lg mx-auto" />
        </div>
      </section>
      <div className="py-12 max-w-3xl mx-auto px-6">
        <div className="animate-pulse space-y-4 p-10 bg-white rounded-2xl border border-gray-100">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`bg-gray-100 rounded ${i === 0 ? "h-7 w-2/3" : i === 5 ? "h-4 w-1/3" : "h-4 w-full"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center px-4">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-cyan-50">
          <i className="ri-file-damage-line text-3xl text-cyan-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-800 mb-2">页面未找到</h1>
        <p className="text-sm text-gray-500 mb-6">您访问的单页不存在或已被删除</p>
        <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-medium rounded-full hover:from-cyan-600 hover:to-blue-700 transition-colors">
          <i className="ri-home-4-line" />返回首页
        </Link>
      </div>
    </div>
  );
}
